import React, { useState, useEffect, useRef } from 'react';
import axiosRaw from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { baseUrl, onboardingSendAppLink } from '../../utils/urls';
import breadcrumbLogo from '../../Images/Breadcrumb_logo.png';
import './onboarding.css';

// ─── Constants ────────────────────────────────────────────────────────────────

const STEPS = ['contact', 'identity', 'photo', 'path', 'languages'];

const LANGS = [
  'English','Spanish','Mandarin','Cantonese','French','Portuguese','Haitian Creole',
  'Arabic','Russian','Vietnamese','Korean','Tagalog','Polish','Italian','German',
  'Hindi','Urdu','Punjabi','Bengali','Japanese','Hebrew','Greek','Ukrainian',
  'Farsi','Turkish','Dutch','Somali','Amharic','Swahili','Thai',
];

const LP_MESSAGES = {
  1:  { title:'The Pioneer',        body:'You are stepping into an opportunity where you can take control of your future, compete at a high level, and build a reputation that is entirely your own. There is no fixed ceiling on what you can accomplish here. The people willing to lead, act decisively, and take ownership can create extraordinary success while helping others achieve some of their biggest goals.' },
  2:  { title:'The Diplomat',       body:'You are entering an opportunity where meaningful relationships, genuine trust, and your ability to understand people can become the foundation of remarkable success. By helping clients feel supported through some of the biggest decisions of their lives, you can build a powerful network, a rewarding future, and relationships that continue creating opportunities for years to come.' },
  3:  { title:'The Communicator',   body:'This is an opportunity to turn your personality, creativity, and ability to connect with people into something truly valuable. Your voice can help make major financial decisions feel clear and exciting for others. As you become known, trusted, and remembered, there is almost no limit to the success you can create.' },
  4:  { title:'The Builder',        body:'You are beginning a proven opportunity where knowledge, consistency, and disciplined execution can build lasting financial security and eventually something much larger. By mastering the process and doing the right things repeatedly, you can create a dependable business, serve families well, and build success that compounds year after year.' },
  5:  { title:'The Freedom-Seeker', body:'You are stepping into a flexible, fast-moving opportunity filled with new people, new challenges, and nearly unlimited room to grow. This path can take you far beyond a traditional job by giving you the freedom to expand your network, increase your income, explore new possibilities, and design a future that continues evolving with you.' },
  6:  { title:'The Nurturer',       body:'This is an opportunity to create an exceptional future for yourself by helping other families create stability, security, and a place to call home. The more people you guide with care, patience, and integrity, the more trust you can build. That trust can grow into a deeply rewarding and financially powerful business.' },
  7:  { title:'The Analyst',        body:'You are entering an opportunity where deep knowledge, thoughtful judgment, and mastery of the details can make you exceptionally valuable. As you learn to understand what others overlook, you can become the trusted expert people seek out for their most important decisions and build significant success through the quality of your insight.' },
  8:  { title:'The Powerhouse',     body:'You are entering a powerful opportunity where ambition, discipline, and measurable performance can create exceptional income, influence, and long-term wealth. This is an industry where people who take the opportunity seriously can completely transform their financial lives while helping clients make some of the most important moves of theirs.' },
  9:  { title:'The Visionary',      body:'This is an opportunity to build an abundant future while helping people create stability, build wealth, and move their families forward. Every person you guide represents a life you can positively affect. Over time, that impact can become both a meaningful legacy and an extraordinary source of success for you.' },
  11: { title:'The Illuminator',    body:'You are stepping into an opportunity where your vision, intuition, and ability to recognize possibilities in people can help turn major life goals into reality. By helping others see a path forward that they may not have seen for themselves, you can create a future of extraordinary impact, influence, and financial possibility.' },
  22: { title:'The Master Builder', body:'This is an opportunity to build something substantial: a successful business, a powerful network, lasting wealth, and a reputation capable of opening doors for decades. With a large enough vision and the discipline to execute it one relationship at a time, this path can become far greater than an opportunity. It can become the foundation of an enduring legacy.' },
  33: { title:'The Master Teacher', body:'This is an opportunity to become a trusted guide during some of the most significant financial decisions people will ever make. By helping clients move forward with confidence and eventually helping others around you become successful, you can build an abundant future whose positive impact extends far beyond your own life.' },
};

const TRAINING_AUDIOS = [
  { title:'Divorce Life Event - Edward Haar (Wayne 2)',                    link:'https://assets.cdn.filesafe.space/lmdPZBnct1qETepFwbdi/media/6a68e5268219a56ed32695d1.mp3' },
  { title:'Hung up - Edward Haar (Blessings Robbins)',                     link:'https://assets.cdn.filesafe.space/lmdPZBnct1qETepFwbdi/media/6a68e6cfa0ca605ff3b770d2.mp3' },
  { title:"I'm busy at work - Edward Haar (Dinesh)",                       link:'https://assets.cdn.filesafe.space/lmdPZBnct1qETepFwbdi/media/6a68e6cfa0ca605ff3b77047.mp3' },
  { title:'No not interested - John Colatosti (Bhag Singh)',               link:'https://assets.cdn.filesafe.space/lmdPZBnct1qETepFwbdi/media/6a68e6cfa0ca605ff3b77036.mp3' },
  { title:'Supposed Wrong Number - Edward Haar (Taina & Mr Robinson)',     link:'https://assets.cdn.filesafe.space/lmdPZBnct1qETepFwbdi/media/6a68e6cf18a264df539f68bf.mp3' },
  { title:'Taking an app - Edward Haar (Ganaa)',                           link:'https://assets.cdn.filesafe.space/lmdPZBnct1qETepFwbdi/media/6a68e6cf609da4cf5806b052.mp3' },
];
const REQUIRED_CALLS = 5;
const REQUIRED_SECONDS = 300;

const TRAINING_VIDEOS = [
  { id:'v1', title:'How to disposition your leads - John Colatosti / Bcrumbs CRM', link:'https://assets.cdn.filesafe.space/lmdPZBnct1qETepFwbdi/media/6a68e6ee8219a56ed3281740.mp4' },
];

const SLA = [
  {
    b:'Attempt every real-time lead 3 times in the first 12 hours.',
    t:'When a live lead hits your desk, you\'ll make at least three genuine contact attempts within the first 12 hours before it cools.',
  },
  {
    b:'Always submit an application.',
    t:'Whenever you learn anything about someone\'s qualification, partial or complete, qualified or not, you submit an application. Every time.',
  },
  {
    b:'Help everyone who comes across your desk.',
    t:'You help every person who reaches you. The money sorts itself out. Service first, always.',
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function computeLP(dob) {
  if (!dob) return 0;
  const digits = dob.replace(/\D/g,'');
  let n = digits.split('').reduce((a,d) => a + parseInt(d,10), 0);
  while (n > 9 && n !== 11 && n !== 22 && n !== 33) {
    n = String(n).split('').reduce((a,d) => a + parseInt(d,10), 0);
  }
  return n;
}

function strengthScore(pw) {
  let s = 0;
  if (pw.length > 12)           s++;
  if (/[A-Z]/.test(pw))         s++;
  if (/[a-z]/.test(pw))         s++;
  if (/[0-9]/.test(pw))         s++;
  if (/[^A-Za-z0-9]/.test(pw))  s++;
  return s;
}


function fmt(sec) {
  const m = Math.floor(sec/60), s = Math.floor(sec%60);
  return `${m}:${s.toString().padStart(2,'0')}`;
}

function todayStr() {
  return new Date().toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'});
}

function genUsername(first, last) {
  const f = (first||'').trim()[0] || '';
  const l = (last||'').replace(/\s+/g,'');
  return (f+l) || 'agent';
}

// ─── OTP 4-box ────────────────────────────────────────────────────────────────

function OtpBoxes({ value, onChange, onComplete }) {
  const inputs = useRef([]);
  const vals = (value||'').split('').concat(['','','','','','']).slice(0,6);

  const handleKey = (i, e) => {
    if (e.key === 'Backspace') {
      const next = [...vals]; next[i] = ''; onChange(next.join(''));
      if (i > 0) inputs.current[i-1]?.focus();
      return;
    }
    if (e.key === 'ArrowLeft'  && i > 0) { inputs.current[i-1]?.focus(); return; }
    if (e.key === 'ArrowRight' && i < 5) { inputs.current[i+1]?.focus(); return; }
  };

  const handleInput = (i, e) => {
    const ch = e.target.value.replace(/\D/g,'').slice(-1);
    const next = [...vals]; next[i] = ch;
    const joined = next.join('');
    onChange(joined);
    if (ch && i < 5) inputs.current[i+1]?.focus();
    if (joined.replace(/\s/g,'').length === 6) onComplete && onComplete(joined);
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g,'').slice(0,6);
    if (!text) return;
    e.preventDefault();
    onChange(text.padEnd(6,'').slice(0,6));
    if (text.length >= 6) onComplete && onComplete(text.slice(0,6));
    else inputs.current[text.length]?.focus();
  };

  return (
    <div className="ob-code-box">
      {[0,1,2,3,4,5].map(i => (
        <input
          key={i}
          ref={el => inputs.current[i] = el}
          className="ob-code-input"
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={vals[i]}
          onChange={ev => handleInput(i,ev)}
          onKeyDown={ev => handleKey(i,ev)}
          onPaste={handlePaste}
        />
      ))}
    </div>
  );
}

// ─── Signature Pad ────────────────────────────────────────────────────────────

function SignPad({ onInk, canvasRef }) {
  const isDrawing = useRef(false);
  const lastPos   = useRef({x:0,y:0});

  // Sync canvas buffer to its visible size. Called lazily on first stroke so the
  // canvas is definitely visible (not hidden inside a closed modal).
  const syncSize = (canvas) => {
    const rect = canvas.getBoundingClientRect();
    if (rect.width > 0 && (canvas.width !== Math.round(rect.width) || canvas.height !== Math.round(rect.height))) {
      canvas.width  = Math.round(rect.width);
      canvas.height = Math.round(rect.height);
    }
  };

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src  = e.touches ? e.touches[0] : e;
    return {
      x: src.clientX - rect.left,
      y: src.clientY - rect.top,
    };
  };

  const start = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current; if (!canvas) return;
    syncSize(canvas);
    isDrawing.current = true;
    lastPos.current   = getPos(e,canvas);
  };

  const move = (e) => {
    if (!isDrawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e,canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#111';
    ctx.lineWidth   = 2.2;
    ctx.lineCap     = 'round';
    ctx.lineJoin    = 'round';
    ctx.stroke();
    lastPos.current = pos;
    onInk && onInk(true);
  };

  const stop = () => { isDrawing.current = false; };

  return (
    <canvas
      ref={canvasRef}
      className="ob-sla-pad"
      onMouseDown={start} onMouseMove={move} onMouseUp={stop} onMouseLeave={stop}
      onTouchStart={start} onTouchMove={move} onTouchEnd={stop}
    />
  );
}

// ─── Script doc ───────────────────────────────────────────────────────────────

function ScriptDoc({ firstName }) {
  const nm = firstName || 'Your Name';
  const cn = <span className="var">Maria</span>;
  const src = <span className="var">Facebook</span>;
  const page = <span className="var">Long Island Homes for Sale</span>;
  const you = <span className="var">{nm}</span>;
  return (
    <div className="ob-script-doc">
      <h3>Greeting</h3>
      <div className="rep">Hey {cn}?</div>
      <div className="rep">Hey {cn}! It's {you}, you reached out to us on {src}, on {page}, you're looking to buy a house?</div>
      <div className="cust">Yes</div>
      <div className="rep">Ok great! I can definitely help. Have you been pre-approved for a mortgage yet?</div>

      <h4>If they say no (more common)</h4>
      <div className="cust">No</div>
      <div className="rep">Okay great, not a problem we can help you with that today. What do you do for work?</div>
      <p className="note">Proceed with the application.</p>

      <h4>If they say yes</h4>
      <div className="cust">Yes</div>
      <div className="rep">Okay great, you are 2 steps ahead! Have you been out to see any houses in person yet? How much are you approved for, what is your price range? What type of home are you looking for?</div>

      <h3>Taking an application</h3>
      <p className="note">Conversational cadence. Move naturally from one area to the next.</p>

      <h4>Work</h4>
      <div className="rep">What do you do for work? Awesome, how long have you been doing that?</div>
      <div className="rep">Okay awesome. How much do you make a year? And do you have any other sources of income?</div>
      <p className="note">Gather a full 2 to 3 years of work history: "Okay and what did you do before that?"</p>

      <h4>Savings</h4>
      <div className="rep">Okay awesome. How much do you have saved so far?</div>
      <div className="rep">Okay great. And do you have any sort of retirement account, like a 401k or anything?</div>
      <div className="rep">Okay great. Do you have any friends or family that would be giving you a gift to help with your down payment or closing costs?</div>

      <h4>Credit</h4>
      <div className="rep">Okay awesome. And how is your credit? Rough estimate, what do you think your credit score is?</div>

      <h4>Current home</h4>
      <div className="rep">Ok great. Where do you live now? Are you renting?</div>
      <div className="rep">Ok excellent. How long have you been living there? How much is your rent?</div>
      <div className="rep">Is anyone else going to be buying the house with you or cosigning? Is there anyone else who is working in your household?</div>

      <h4>Home search</h4>
      <div className="rep">Ok great. Now the fun part, what kind of house do you think you would want? What type of home are you looking for?</div>
      <div className="rep">Are there any particular areas you are interested in?</div>
      <div className="rep">Do you have any kids? Who would be living in the house with you?</div>

      <h4>Completing the app</h4>
      <div className="rep">Okay awesome. What's your date of birth?</div>
      <div className="rep">Great, and what's your social?</div>

      <h3>Requesting the documents</h3>
      <p className="section-body">Request the supporting documents while you are still on the call.</p>
      <div className="rep">Okay great! Now we just need to collect a few documents, and then we will review your full application, your credit, your documents, and me and the senior loan officer will give you a call back to go over everything!</div>

      <h4>Employee, salary, or wage earner document list</h4>
      <div className="doc">Last 2 years of tax returns</div>
      <div className="rep">Who usually does your taxes for you? How do they usually send them, in an email or a physical copy? If it's easier you can put me in touch with your tax person, we work with accountants all the time.</div>
      <p className="note">If they have their taxes accessible:</p>
      <div className="rep">Okay great, send it over. I'm going to send you an email right now with the list of documents we need, you can respond to that email.</div>
      <p className="note">If they don't file taxes:</p>
      <div className="rep">Oh okay, not a problem, we have programs for that also for business owners or self employed. We can go strictly off your bank statements.</div>
      <p className="note">If they hesitate about documents:</p>
      <div className="rep">Oh pardon my manners, let me send you all my information (send digital business card) so you can put a face to the name. That is the address to our office and the name of our company (send location card). If it's more comfortable you're welcome to come to our office, but usually it's more convenient to send everything over email or fax.</div>
      <div className="doc">Last 2 years of W2s or 1099s</div>
      <div className="rep">This is usually in your HR portal where you clock in.</div>
      <div className="doc">1 month of paystubs</div>
      <div className="rep">How do you get paid, checks or direct deposit? You can usually download it from your HR portal.</div>
      <div className="doc">Last 2 months of bank statements</div>
      <div className="rep">You can usually export from your bank app on your phone.</div>
      <div className="doc">Clear picture of your ID and SS card</div>
      <div className="rep">You are probably sitting on your ID right now.</div>

      <div className="script-space" />
      <div className="rep">If you have 2 minutes we can grab everything together now and me and the loan officer can call you back later today with good news!</div>
      <p className="note">If they say no:</p>
      <div className="rep">Are you sure? It will only take 2 minutes.</div>
      <p className="note">If they say no:</p>
      <div className="rep">Okay, do you take a lunch break during the day usually?</div>
      <p className="note">If they say no:</p>
      <div className="rep">Okay no worries, what time do you get home?</div>
      <p className="note">If they say no:</p>
      <div className="rep">Haha okay, I understand life is busy. When will you have 5 minutes to work on this? I will make myself available so I can confirm everything is perfect.</div>
      <div className="rep">Okay great, I will mark this in our calendar, I'll reach out to remind you of the appointment. Did you have any questions for me?</div>

      <h4>Business owner or self employed document list</h4>
      <div className="doc">Last 2 years of tax returns</div>
      <div className="rep">Same approach as above. If it's easier, connect us with their tax preparer.</div>
      <div className="doc">Last 2 months of bank statements</div>
      <div className="rep">You can export from your bank app. You can also provide 12 to 24 months of bank statements if you typically write off all your income on taxes or do not file taxes, and we can use that to qualify. Sometimes that is easier for business owners.</div>
      <div className="doc">Clear picture of your ID and SS card</div>

      <h3>If the application seems qualified</h3>
      <div className="rep">Okay great, now more importantly, what do you have going on this weekend? When do you think you would have time to go see some houses in person, is Saturday or Sunday better? Okay great, what time?</div>
      <p className="note">If they say no:</p>
      <div className="rep">Oh of course! Remember this is the biggest purchase of your life, so it's not about rushing. A lot of people don't realize going to see houses in person is completely free. It's really just taking advantage and seeing what's available in your price range, what the payments would be. Seeing houses in person is WAY different than the pictures online. And it's usually fun, it helps you imagine your future and see the neighborhoods!</div>

      <h3>Following up for documents</h3>
      <div className="rep">Hi {cn}, glad I caught you! I know life is busy, you probably didn't get a chance to look at the email. Are you going to be available to go see houses this week or this weekend? If you have 2 minutes now I can probably grab everything we need. Do you have 2 minutes now?</div>
      <div className="rep">Are you sure? It will only take 2 minutes.</div>
      <div className="rep">Ah okay no problem, when do you usually have a lunch break?</div>
      <p className="note">If they say no:</p>
      <div className="rep">Okay, will you have time this evening when you get home?</div>
      <p className="note">If they say no:</p>
      <div className="rep">Okay no problem, you tell me when is best, it will really only take 2 or 3 minutes. Also, more importantly, when do you think you'll be available to go see houses? Before I let you go, who prepares your taxes? If it's easier for you they'll probably have everything we need.</div>

      <p className="note" style={{marginTop:'24px',textAlign:'center'}}>End of script</p>
    </div>
  );
}

// ─── Portal Task ──────────────────────────────────────────────────────────────

function PortalTask({ num, title, badge, desc, done, locked, active, onOpen, ctaLabel, children }) {
  return (
    <div className={`ob-task${done?' done':''}${locked?' locked':''}${active?' active':''}`}>
      <div className="ob-task-main">
        {done ? (
          <div className="ob-tick-slot">
            <div className="ob-tick">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
          </div>
        ) : (
          <div className="ob-step-no">
            {locked
              ? <svg className="ob-lock-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              : num}
          </div>
        )}
        <div className="ob-task-body">
          <div className="tt">{title}{badge && <span className="badge">{badge}</span>}</div>
          <div className="td">{desc}</div>
        </div>
        {!locked && ctaLabel && (
          <button className={`ob-task-cta${active?' primary':''}`} onClick={onOpen}>
            {ctaLabel}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Onboarding() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const otpOptional = searchParams.get('optional') === 'true';
  const api = axiosRaw.create({ baseURL: baseUrl });

  const [phase, setPhase]   = useState('loading');
  const [invite, setInvite] = useState(null);
  const [errMsg, setErrMsg] = useState('');

  // signup step 0-4
  const [step, setStep] = useState(0);

  // contact
  const [phone, setPhone]         = useState('');
  const [email, setEmail]         = useState('');
  const [hasEmail, setHasEmail]   = useState(true);
  const [phoneOk, setPhoneOk]     = useState(false);
  const [emailOk, setEmailOk]     = useState(false);
  const [phoneSent, setPhoneSent] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [phoneCode, setPhoneCode] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [phoneCD, setPhoneCD]     = useState(0);
  const [emailCD, setEmailCD]     = useState(0);
  const [otpSending, setOtpSending]     = useState({phone:false,email:false});
  const [otpVerifying, setOtpVerifying] = useState({phone:false,email:false});
  const [otpSendErr, setOtpSendErr]     = useState({phone:'',email:''});
  const [otpVerifyErr, setOtpVerifyErr] = useState({phone:'',email:''});

  // identity
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [dob,       setDob]       = useState('');
  const [password,  setPassword]  = useState('');
  const [showPw,    setShowPw]    = useState(false);

  // photo
  const [photo,     setPhoto]     = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [dragOver,  setDragOver]  = useState(false);
  const [showAiPop,setShowAiPop]= useState(false);
  const fileRef = useRef();

  // path
  const [path, setPath] = useState('');

  // languages
  const [langs,       setLangs]       = useState(['English']);
  const [langSearch,  setLangSearch]  = useState('');
  const [langDropOpen,setLangDropOpen]= useState(false);
  const langRef = useRef();

  // submit
  const [submitting,   setSubmitting]   = useState(false);
  const [submitError,  setSubmitError]  = useState('');

  // portal task done states
  const [taskDone, setTaskDone] = useState({phone:false,script:false,calls:false,videos:false,sla:false});

  // calls
  const [callProgress,   setCallProgress]   = useState({});
  const [audioDurations, setAudioDurations] = useState({});
  const [playingIdx,     setPlayingIdx]     = useState(null);
  const [completedCalls, setCompletedCalls] = useState({});
  const audioRef = useRef(null);

  // videos
  const [vidOpen,    setVidOpen]    = useState(null);
  const [watchedVids,setWatchedVids]= useState({});
  const vidRef = useRef(null);

  // script
  const [scriptModal,      setScriptModal]      = useState(false);
  const [scriptScrolled,   setScriptScrolled]   = useState(false);
  const [scriptConfirmed,  setScriptConfirmed]  = useState(false);

  // sla
  const [slaModal,     setSlaModal]     = useState(false);
  const [slaInitials,  setSlaInitials]  = useState(['','','']);
  const [slaTicked,    setSlaTicked]    = useState([false,false,false]);
  const [slaHasInk,    setSlaHasInk]   = useState(false);
  const [slaSubmitting,setSlaSubmitting]= useState(false);
  const [slaError,     setSlaError]     = useState('');
  const sigRef = useRef();

  // phone task confirm
  const [phoneConfirmed, setPhoneConfirmed] = useState(false);
  const [appLinkSent,   setAppLinkSent]    = useState(null);
  const [appLinkSending, setAppLinkSending] = useState(false);

  // ── Send app link ────────────────────────────────────────────────────────────
  const sendAppLink = async (os) => {
    setAppLinkSent(os);
    setAppLinkSending(true);
    try {
      const authTokens = JSON.parse(localStorage.getItem('authTokens') || '{}');
      await api.post(onboardingSendAppLink, { platform: os === 'iPhone' ? 'ios' : 'android' }, {
        headers: { Authorization: `Bearer ${authTokens.access}` },
      });
    } catch(e) {
      // sent state already set — don't revert on error
    } finally {
      setAppLinkSending(false);
    }
  };

  // ── Cooldown ticks ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (phoneCD <= 0) return;
    const t = setTimeout(() => setPhoneCD(c => c-1), 1000);
    return () => clearTimeout(t);
  }, [phoneCD]);
  useEffect(() => {
    if (emailCD <= 0) return;
    const t = setTimeout(() => setEmailCD(c => c-1), 1000);
    return () => clearTimeout(t);
  }, [emailCD]);

  // ── Validate token ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) { setErrMsg('No invite token provided.'); setPhase('error'); return; }
    api.get(`/api/onboarding/invite/${token}/`)
      .then(res => {
        setInvite(res.data);
        setPhone(res.data.phone || '');
        setEmail(res.data.email || '');
        setFirstName(res.data.first_name || '');
        setLastName(res.data.last_name || '');
        setHasEmail(res.data.has_email !== false);
        if (res.data.phone_verified) setPhoneOk(true);
        if (res.data.email_verified) setEmailOk(true);
        setPhase('signup');
      })
      .catch(err => {
        setErrMsg(err?.response?.data?.detail || err?.response?.data?.error || 'Invalid or expired invite link.');
        setPhase('error');
      });
  }, []); // eslint-disable-line

  // ── Outside click for lang dropdown ────────────────────────────────────────
  useEffect(() => {
    const h = (e) => { if (langRef.current && !langRef.current.contains(e.target)) setLangDropOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // ── Audio cleanup ───────────────────────────────────────────────────────────
  useEffect(() => () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } }, []);
  useEffect(() => () => { if (vidRef.current) vidRef.current.pause(); }, []);

  // ── OTP send / verify ───────────────────────────────────────────────────────
  const sendOtp = async (channel) => {
    setOtpSending(s => ({...s,[channel]:true}));
    setOtpSendErr(s => ({...s,[channel]:''}));
    const apiChannel = channel === 'phone' ? 'sms' : channel;
    const value = channel === 'phone' ? phone : email;
    try {
      await api.post('/api/onboarding/otp/send/', { token, channel: apiChannel, value });
      if (channel === 'phone') { setPhoneSent(true); setPhoneCD(60); }
      else                     { setEmailSent(true); setEmailCD(60); }
    } catch(e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.error || 'Failed to send code. Please try again.';
      setOtpSendErr(s => ({...s,[channel]:msg}));
    }
    finally { setOtpSending(s => ({...s,[channel]:false})); }
  };

  const verifyOtp = async (channel, code) => {
    if (code.length < 6) return;
    setOtpVerifying(s => ({...s,[channel]:true}));
    setOtpVerifyErr(s => ({...s,[channel]:''}));
    const apiChannel = channel === 'phone' ? 'sms' : channel;
    try {
      const res = await api.post('/api/onboarding/otp/verify/', { token, channel: apiChannel, otp: code });
      if (channel === 'phone' && res.data.phone_verified) setPhoneOk(true);
      if (channel === 'email' && res.data.email_verified) setEmailOk(true);
    } catch(e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.error || 'Invalid code. Please try again.';
      setOtpVerifyErr(s => ({...s,[channel]:msg}));
    }
    finally { setOtpVerifying(s => ({...s,[channel]:false})); }
  };

  // ── Photo ───────────────────────────────────────────────────────────────────
  const handleFile = (file) => {
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = e => setPhoto(e.target.result);
    reader.readAsDataURL(file);
  };

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('token', token);
      fd.append('first_name', firstName);
      fd.append('last_name', lastName);
      fd.append('dob', dob);
      fd.append('password', password);
      fd.append('path', path);
      fd.append('languages', JSON.stringify(langs));
      if (photoFile) fd.append('profile_image', photoFile);
      const res = await api.post('/api/onboarding/complete/', fd);
      if (res.data.access) {
        localStorage.setItem('authTokens', JSON.stringify({ access: res.data.access, refresh: res.data.refresh }));
      }
      setPhase('welcome');
    } catch(e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.error
        || (typeof e?.response?.data === 'string' ? e.response.data : null)
        || 'Something went wrong. Please try again.';
      setSubmitError(msg);
    }
    finally { setSubmitting(false); }
  };

  // ── Step navigation ─────────────────────────────────────────────────────────
  const canGoNext = [
    otpOptional ? true : (emailOk && phoneOk),
    !!(firstName.trim() && lastName.trim() && dob && strengthScore(password) === 5),
    !!photo,
    !!path,
    langs.length > 0,
  ][step];

  const goNext = () => { stopAudio(); if (step < STEPS.length-1) setStep(s=>s+1); else handleSubmit(); };
  const goBack = () => { stopAudio(); if (step > 0) setStep(s=>s-1); };

  // ── Calls ───────────────────────────────────────────────────────────────────
  const totalListened = Object.values(callProgress).reduce((a,b) => a+b, 0);
  const heardCount    = Object.keys(completedCalls).filter(i => completedCalls[i]).length;
  const callsOk       = otpOptional || heardCount >= REQUIRED_CALLS;

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.ontimeupdate = null;
      audioRef.current.onended = null;
      audioRef.current = null;
    }
    setPlayingIdx(null);
  };

  const playCall = (idx) => {
    if (playingIdx === idx) {
      stopAudio();
      return;
    }
    stopAudio();
    setPlayingIdx(idx);
    const audio = new Audio(TRAINING_AUDIOS[idx].link);
    audioRef.current = audio;
    audio.onloadedmetadata = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setAudioDurations(prev => ({...prev, [idx]: Math.floor(audio.duration)}));
      }
    };
    audio.ontimeupdate = () => {
      const t = Math.floor(audio.currentTime);
      setCallProgress(prev => (prev[idx] === t ? prev : {...prev, [idx]: t}));
    };
    audio.onended = () => {
      setCompletedCalls(prev => ({...prev, [idx]: true}));
      setPlayingIdx(null);
      audioRef.current = null;
    };
    audio.play().catch(console.error);
  };

  // ── Videos ──────────────────────────────────────────────────────────────────
  const openVideo = (vid) => {
    if (vidRef.current) vidRef.current.pause();
    setVidOpen(vid);
  };

  const closeVid = () => {
    if (vidRef.current) vidRef.current.pause();
    setVidOpen(null);
  };

  // ── SLA ─────────────────────────────────────────────────────────────────────
  const slaReady = slaTicked.every(Boolean) && slaInitials.every(s=>s.trim().length>0) && slaHasInk;

  const clearSig = () => {
    const canvas = sigRef.current; if (!canvas) return;
    canvas.getContext('2d').clearRect(0,0,canvas.width,canvas.height);
    setSlaHasInk(false);
  };

  const handleSlaSubmit = async () => {
    setSlaSubmitting(true);
    try {
      const canvas = sigRef.current;
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      const fd = new FormData();
      const initials = ((firstName[0]||'') + (lastName[0]||'')).toUpperCase();
      fd.append('initials', initials);
      fd.append('agreement_version', 'v1');
      fd.append('initials_data', JSON.stringify({ clauses: slaInitials }));
      fd.append('signature_image', blob, 'signature.png');
      const authTokens = JSON.parse(localStorage.getItem('authTokens') || '{}');
      await api.post('/api/onboarding/sla/', fd, {
        headers: { Authorization: `Bearer ${authTokens.access}` },
      });
      setTaskDone(p => ({...p, sla:true}));
      setSlaModal(false);
    } catch(e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.error || 'Something went wrong. Please try again.';
      setSlaError(msg);
    }
    finally { setSlaSubmitting(false); }
  };

  // ── Portal helpers ───────────────────────────────────────────────────────────
  const PORTAL_TASKS = ['phone','script','calls','videos','sla'];
  const doneCount    = PORTAL_TASKS.filter(k=>taskDone[k]).length;
  const requiredDone = ['phone','script','calls','sla'].every(k=>taskDone[k]);
  const RING_R       = 22;
  const RING_CIRC    = 2 * Math.PI * RING_R;
  const ringPct      = (doneCount / PORTAL_TASKS.length) * 100;

  const isLocked = (key) => {
    const idx = PORTAL_TASKS.indexOf(key);
    for (let i = 0; i < idx; i++) { if (!taskDone[PORTAL_TASKS[i]]) return true; }
    return false;
  };
  const isActive = (key) => !taskDone[key] && !isLocked(key);

  // ── Script PDF download ──────────────────────────────────────────────────────
  const downloadScript = () => {
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const pw      = doc.internal.pageSize.getWidth();
    const ph      = doc.internal.pageSize.getHeight();
    const margin  = 54;
    const cw      = pw - margin * 2;
    let y         = margin;
    const lh      = 15;

    const chk = (extra = 0) => {
      if (y + extra > ph - margin) { doc.addPage(); y = margin; }
    };

    const write = (text, { bold = false, size = 10, rgb = [30,30,30], indent = 0, after = 6 } = {}) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(size);
      doc.setTextColor(...rgb);
      const lines = doc.splitTextToSize(text, cw - indent);
      lines.forEach(line => { chk(lh); doc.text(line, margin + indent, y); y += lh; });
      y += after;
    };

    const H3   = t => { chk(30); y += 8;  write(t, { bold:true, size:13, after:5 }); };
    const H4   = t => { chk(24); y += 4;  write(t, { bold:true, size:11, rgb:[60,60,60], after:3 }); };
    const Rep  = t => write(t,  { size:10, after:5 });
    const Cust = t => write(`Customer: "${t}"`, { size:10, rgb:[90,90,90], indent:14, after:5 });
    const Note = t => write(`Note: ${t}`, { size:9, rgb:[130,130,130], indent:14, after:5 });
    const Doc  = t => write(`• ${t}`, { size:10, indent:14, after:4 });

    // Title
    write('Sales Script', { bold:true, size:20, after:4 });
    write('Breadcrumbs CRM', { size:10, rgb:[140,140,140], after:24 });

    H3('Greeting');
    Rep('Hey Maria?');
    Rep("Hey Maria! It's [Your Name], you reached out to us on Facebook, on Long Island Homes for Sale, you're looking to buy a house?");
    Cust('Yes');
    Rep("Ok great! I can definitely help. Have you been pre-approved for a mortgage yet?");

    H4('If they say no (more common)');
    Cust('No');
    Rep("Okay great, not a problem we can help you with that today. What do you do for work?");
    Note('Proceed with the application.');

    H4('If they say yes');
    Cust('Yes');
    Rep("Okay great, you are 2 steps ahead! Have you been out to see any houses in person yet? How much are you approved for, what is your price range? What type of home are you looking for?");

    H3('Taking an application');
    Note('Conversational cadence. Move naturally from one area to the next.');

    H4('Work');
    Rep("What do you do for work? Awesome, how long have you been doing that?");
    Rep("Okay awesome. How much do you make a year? And do you have any other sources of income?");
    Note('Gather a full 2 to 3 years of work history: "Okay and what did you do before that?"');

    H4('Savings');
    Rep("Okay awesome. How much do you have saved so far?");
    Rep("Okay great. And do you have any sort of retirement account, like a 401k or anything?");
    Rep("Okay great. Do you have any friends or family that would be giving you a gift to help with your down payment or closing costs?");

    H4('Credit');
    Rep("Okay awesome. And how is your credit? Rough estimate, what do you think your credit score is?");

    H4('Current home');
    Rep("Ok great. Where do you live now? Are you renting?");
    Rep("Ok excellent. How long have you been living there? How much is your rent?");
    Rep("Is anyone else going to be buying the house with you or cosigning? Is there anyone else who is working in your household?");

    H4('Home search');
    Rep("Ok great. Now the fun part, what kind of house do you think you would want? What type of home are you looking for?");
    Rep("Are there any particular areas you are interested in?");
    Rep("Do you have any kids? Who would be living in the house with you?");

    H4('Completing the app');
    Rep("Okay awesome. What's your date of birth?");
    Rep("Great, and what's your social?");

    H3('Requesting the documents');
    Rep("Okay great! Now we just need to collect a few documents, and then we will review your full application, your credit, your documents, and me and the senior loan officer will give you a call back to go over everything!");

    H4('Employee, salary, or wage earner document list');
    Doc('Last 2 years of tax returns');
    Rep("Who usually does your taxes for you? How do they usually send them, in an email or a physical copy? If it's easier you can put me in touch with your tax person, we work with accountants all the time.");
    Note("If they have their taxes accessible:");
    Rep("Okay great, send it over. I'm going to send you an email right now with the list of documents we need, you can respond to that email.");
    Note("If they don't file taxes:");
    Rep("Oh okay, not a problem, we have programs for that also for business owners or self employed. We can go strictly off your bank statements.");
    Note("If they hesitate about documents:");
    Rep("Oh pardon my manners, let me send you all my information (send digital business card) so you can put a face to the name. That is the address to our office and the name of our company (send location card). If it's more comfortable you're welcome to come to our office, but usually it's more convenient to send everything over email or fax.");
    Doc('Last 2 years of W2s or 1099s');
    Rep("This is usually in your HR portal where you clock in.");
    Doc('1 month of paystubs');
    Rep("How do you get paid, checks or direct deposit? You can usually download it from your HR portal.");
    Doc('Last 2 months of bank statements');
    Rep("You can usually export from your bank app on your phone.");
    Doc('Clear picture of your ID and SS card');
    Rep("You are probably sitting on your ID right now.");

    y += 8;
    Rep("If you have 2 minutes we can grab everything together now and me and the loan officer can call you back later today with good news!");
    Note("If they say no:"); Rep("Are you sure? It will only take 2 minutes.");
    Note("If they say no:"); Rep("Okay, do you take a lunch break during the day usually?");
    Note("If they say no:"); Rep("Okay no worries, what time do you get home?");
    Note("If they say no:"); Rep("Haha okay, I understand life is busy. When will you have 5 minutes to work on this? I will make myself available so I can confirm everything is perfect.");
    Rep("Okay great, I will mark this in our calendar, I'll reach out to remind you of the appointment. Did you have any questions for me?");

    H4('Business owner or self employed document list');
    Doc('Last 2 years of tax returns');
    Rep("Same approach as above. If it's easier, connect us with their tax preparer.");
    Doc('Last 2 months of bank statements');
    Rep("You can export from your bank app. You can also provide 12 to 24 months of bank statements if you typically write off all your income on taxes or do not file taxes, and we can use that to qualify. Sometimes that is easier for business owners.");
    Doc('Clear picture of your ID and SS card');

    H3('If the application seems qualified');
    Rep("Okay great, now more importantly, what do you have going on this weekend? When do you think you would have time to go see some houses in person, is Saturday or Sunday better? Okay great, what time?");
    Note("If they say no:");
    Rep("Oh of course! Remember this is the biggest purchase of your life, so it's not about rushing. A lot of people don't realize going to see houses in person is completely free. It's really just taking advantage and seeing what's available in your price range, what the payments would be. Seeing houses in person is WAY different than the pictures online. And it's usually fun, it helps you imagine your future and see the neighborhoods!");

    H3('Following up for documents');
    Rep("Hi Maria, glad I caught you! I know life is busy, you probably didn't get a chance to look at the email. Are you going to be available to go see houses this week or this weekend? If you have 2 minutes now I can probably grab everything we need. Do you have 2 minutes now?");
    Rep("Are you sure? It will only take 2 minutes.");
    Rep("Ah okay no problem, when do you usually have a lunch break?");
    Note("If they say no:"); Rep("Okay, will you have time this evening when you get home?");
    Note("If they say no:"); Rep("Okay no problem, you tell me when is best, it will really only take 2 or 3 minutes. Also, more importantly, when do you think you'll be available to go see houses? Before I let you go, who prepares your taxes? If it's easier for you they'll probably have everything we need.");

    y += 16;
    write('— End of script —', { size:9, rgb:[160,160,160], after:0 });

    doc.save('Sales_Script.pdf');
  };

  // ── Languages ────────────────────────────────────────────────────────────────
  const langOptions = LANGS.filter(l => !langs.includes(l) && l.toLowerCase().includes(langSearch.toLowerCase()));
  const addLang    = (l) => { setLangs(p=>[...p,l]); setLangSearch(''); setLangDropOpen(false); };
  const removeLang = (l) => { if (l === 'English') return; setLangs(p=>p.filter(x=>x!==l)); };

  // ── Lifepath ─────────────────────────────────────────────────────────────────
  const lp     = computeLP(dob);
  const lpInfo = LP_MESSAGES[lp] || { title:'Unique Path', body:'Your path is one of a kind — your energy and drive will define your success here.' };

  const pwStrength = strengthScore(password);
  const username   = genUsername(firstName, lastName);

  // ════════════════════════════════════════════════════════════════════════════
  // ── Loading / Error ─────────────────────────────────────────────────────────
  if (phase === 'loading') return (
    <div className="ob-loading-screen">
      <div className="ob-spinner" />
      <span style={{color:'var(--txt-dim)',fontSize:14}}>Verifying your invite…</span>
    </div>
  );

  if (phase === 'error') return (
    <div className="ob-error-screen">
      <div className="ob-error-icon">⚠️</div>
      <h2>Invite unavailable</h2>
      <p>{errMsg}</p>
    </div>
  );

  // ── Lifepath Welcome ─────────────────────────────────────────────────────────
  if (phase === 'welcome') return (
    <div className="ob-page">
      <div className="ob-grain" />
      <div className="ob-shell">
        <div className="ob-topbar">
          <div className="ob-brand">
            <img src={breadcrumbLogo} alt="Bcrumbs" className="ob-brand-logo" />
          </div>
          <button className="ob-exit">Save &amp; exit</button>
        </div>
        <div className="ob-lpwelcome show">
          <div className="ob-lp-inner">
            {/* <div className="ob-eyebrow" style={{textAlign:'center',marginBottom:10}}>Life Path {lp} · {lpInfo.title}</div> */}
            <div className="ob-lp-hi">Welcome, {firstName}.</div>
            <div className="ob-lp-body">{lpInfo.body}</div>
            <button className="ob-lp-btn" onClick={()=>setPhase('portal')}>Let's get started</button>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Portal ───────────────────────────────────────────────────────────────────
  if (phase === 'portal') return (
    <div className="ob-page">
      <div className="ob-grain" />
      <div className="ob-shell">
        <div className="ob-topbar">
          <div className="ob-brand">
            <img src={breadcrumbLogo} alt="Breadcrumbs" className="ob-brand-logo" />
          </div>
          <button className="ob-exit">Save &amp; exit</button>
        </div>

        <div className="ob-portal show">
          {/* Progress ring */}
          <div className="ob-prog-summary">
            <div className="ob-ring">
              <svg width="56" height="56">
                <circle cx="28" cy="28" r={RING_R} fill="none" stroke="rgba(255,255,255,.1)" strokeWidth="5"/>
                <circle cx="28" cy="28" r={RING_R} fill="none"
                  stroke={ringPct===100?'var(--ok)':'var(--accent)'}
                  strokeWidth="5"
                  strokeDasharray={RING_CIRC}
                  strokeDashoffset={RING_CIRC - (RING_CIRC * ringPct / 100)}
                  strokeLinecap="round"
                  style={{transition:'.4s'}}
                  transform="rotate(-90 28 28)"
                />
              </svg>
              <div className="num">{doneCount}/{PORTAL_TASKS.length}</div>
            </div>
            <div className="ob-prog-txt">
              <div className="t">Setup progress</div>
              <div className="d">
                {requiredDone
                  ? "All steps complete. You're ready to graduate."
                  : 'Complete all steps to graduate to full access.'}
              </div>
            </div>
          </div>

          {/* Task 1 – Account (pre-done) */}
          <PortalTask num={1} title="Create your account" badge="Done" desc="Profile, path, and languages are all set." done={true} locked={false} />

          {/* Task 2 – Phone Login */}
          <PortalTask
            num={2} title="Log in to Breadcrumbs Phone" badge="Access"
            desc="All calls and leads are assigned through Breadcrumbs Phone, powered by Lead Connector. Let's get you logged in on your phone and your computer."
            done={taskDone.phone} locked={false} active={isActive('phone')}
          >
            {isActive('phone') && (
              <div className="ob-drawer show">
                <div className="ob-sub-head">On your computer</div>
                <p style={{color:'var(--txt-dim)',fontSize:13.5,marginBottom:12,lineHeight:1.6}}>
                  Go to <span className="ob-url-chip">phone.bcrumbs.com</span> and sign in with <b style={{color:'var(--txt)'}}>{email}</b> and the password you just created.
                </p>
                <button className="ob-open-ext" onClick={e=>{window.open('https://phone.bcrumbs.com','_blank');e.currentTarget.textContent='Opened in a new tab ↗';}}>Open phone.bcrumbs.com <span style={{opacity:.7}}>↗</span></button>
                <div className="ob-sub-head">On your phone</div>
                <p style={{color:'var(--txt-dim)',fontSize:13.5,marginBottom:12,lineHeight:1.6}}>
                  Get the same system in your pocket. We'll text a download link to <b style={{color:'var(--txt)'}}>{phone||'your number'}</b>.
                </p>
                <div className="ob-btn-pair" style={{marginBottom:12}}>
                  <button className={`ob-os-btn${appLinkSent==='iPhone'?'':' ghost'}`} disabled={appLinkSending} onClick={()=>sendAppLink('iPhone')}>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M16.4 12.9c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.9-3.5.9s-1.8-.8-3-.8c-1.5 0-3 .9-3.8 2.3-1.6 2.8-.4 7 1.2 9.3.8 1.1 1.7 2.4 2.9 2.3 1.2 0 1.6-.7 3-.7s1.8.7 3 .7 2-1 2.8-2.1c.9-1.3 1.2-2.5 1.3-2.6-.1 0-2.5-1-2.5-3.9zM14.6 5.6c.6-.8 1-1.9.9-3-.9 0-2 .6-2.7 1.4-.6.7-1.1 1.8-.9 2.9 1 .1 2-.5 2.7-1.3z"/></svg>
                    iPhone
                  </button>
                  <button className={`ob-os-btn${appLinkSent==='Android'?'':' ghost'}`} disabled={appLinkSending} onClick={()=>sendAppLink('Android')}>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M6 9v7a1 1 0 0 0 1 1h1v3a1 1 0 0 0 2 0v-3h4v3a1 1 0 0 0 2 0v-3h1a1 1 0 0 0 1-1V9H6zM4.5 9A1.5 1.5 0 0 0 3 10.5v4a1.5 1.5 0 0 0 3 0v-4A1.5 1.5 0 0 0 4.5 9zm15 0a1.5 1.5 0 0 0-1.5 1.5v4a1.5 1.5 0 0 0 3 0v-4A1.5 1.5 0 0 0 19.5 9zM15.5 3.7l1-1.5a.3.3 0 0 0-.5-.3l-1 1.6a5.9 5.9 0 0 0-4 0l-1-1.6a.3.3 0 0 0-.5.3l1 1.5A5.5 5.5 0 0 0 6.5 8h11a5.5 5.5 0 0 0-2-4.3zM9.8 6a.6.6 0 1 1 0-1.2.6.6 0 0 1 0 1.2zm4.4 0a.6.6 0 1 1 0-1.2.6.6 0 0 1 0 1.2z"/></svg>
                    Android
                  </button>
                </div>
                {appLinkSent && (
                  <div className="ob-sent-note">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="15" height="15"><path d="M20 6L9 17l-5-5"/></svg>
                    <span>{appLinkSent} download link sent to {phone||'your phone'}. Check your texts.</span>
                  </div>
                )}
                <div className={`ob-confirm-check${phoneConfirmed?' on':''}`} onClick={()=>setPhoneConfirmed(v=>!v)}>
                  <div className="cbox">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  I'm logged in on my computer and my phone
                </div>
                <div className="ob-drawer-foot">
                  <div className="df-note">Confirm you're logged in on both to continue.</div>
                  <button className="ob-mark-done" disabled={!phoneConfirmed}
                    onClick={()=>{ setTaskDone(p=>({...p,phone:true})); }}>
                    Continue
                  </button>
                </div>
              </div>
            )}
          </PortalTask>

          {/* Task 3 – Script */}
          <PortalTask
            num={3} title="Learn the script" badge="Script"
            desc="Read the full script and save a copy to keep on hand for your calls."
            done={taskDone.script} locked={isLocked('script')} active={isActive('script')}
          >
            {isActive('script') && (
              <div className="ob-drawer show">
                <div className="ob-btn-pair" style={{marginBottom:16}}>
                  <button className="ob-os-btn" onClick={()=>setScriptModal(true)}>Open the script</button>
                  <button className="ob-os-btn ghost" onClick={downloadScript}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M12 3v12M7 10l5 5 5-5M5 21h14"/></svg>
                    Save a copy
                  </button>
                </div>
                <div
                  className={`ob-confirm-check${scriptConfirmed?' on':''}${!scriptScrolled?' locked':''}`}
                  onClick={()=>{ if(scriptScrolled) setScriptConfirmed(v=>!v); }}
                  style={{opacity: scriptScrolled ? 1 : 0.45, pointerEvents: scriptScrolled ? 'auto' : 'none'}}
                >
                  <div className="cbox">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  I have read the script and saved a copy for reference
                </div>
                <div className="ob-lock-note">Open and read the script to enable this.</div>
                <div className="ob-drawer-foot">
                  <div className="df-note">Confirm above to continue.</div>
                  <button className="ob-mark-done" disabled={!scriptConfirmed}
                    onClick={()=>{ setTaskDone(p=>({...p,script:true})); }}>
                    Continue
                  </button>
                </div>
              </div>
            )}
          </PortalTask>

          {/* Task 4 – Calls */}
          <PortalTask
            num={4} title="Listen to training calls" badge="Calls"
            desc="Hear how top agents run their calls. Listen to a few minutes across at least 5 calls."
            done={taskDone.calls} locked={isLocked('calls')} active={isActive('calls')}
          >
            {isActive('calls') && (
              <div className="ob-drawer show">
                <div className="ob-drawer-head">
                  <div className="dh-t">Training call library</div>
                  <div className="dh-m">At least {REQUIRED_CALLS} calls · 5 min total</div>
                </div>
                <div className="ob-listen-meter">
                  <div className="bar">
                    <i style={{width:`${Math.min(100,(heardCount/REQUIRED_CALLS)*100)}%`}} />
                  </div>
                  <div className="ob-lm-txt"><b>{heardCount}</b>/{REQUIRED_CALLS} calls · <b>{fmt(totalListened)}</b> / 5:00</div>
                </div>
                {TRAINING_AUDIOS.map((call,i) => {
                  const elapsed  = callProgress[i]||0;
                  const heard    = !!completedCalls[i];
                  const duration = audioDurations[i] || 0;
                  const pct      = duration > 0 ? Math.min(100,(elapsed/duration)*100) : 0;
                  const isPlay   = playingIdx===i;
                  return (
                    <div key={i} className={`ob-call${heard?' heard':''}`}>
                      <button className={`ob-play${isPlay?' playing':''}${heard?' complete':''}`} onClick={()=>playCall(i)}>
                        {isPlay
                          ? <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                          : heard
                            ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="16" height="16"><polyline points="20 6 9 17 4 12"/></svg>
                            : <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                        }
                      </button>
                      <div className="ob-call-info">
                        <div className="ci-t">{call.title}</div>
                        <div className="ci-bar"><i style={{width:`${pct}%`}} /></div>
                      </div>
                      <div className="ob-call-time">
                        {heard
                          ? <span className="ci-listened">listened</span>
                          : elapsed > 0 ? <b>{fmt(elapsed)}</b> : null
                        }
                      </div>
                    </div>
                  );
                })}
                <div className="ob-drawer-foot">
                  <div className="df-note">You can pause and come back. Progress is saved.</div>
                  <button className="ob-mark-done" disabled={!callsOk}
                    onClick={()=>{ stopAudio(); setTaskDone(p=>({...p,calls:true})); }}>
                    Continue
                  </button>
                </div>
              </div>
            )}
          </PortalTask>

          {/* Task 5 – Videos (optional) */}
          <PortalTask
            num={5} title="Learn how easy it is to use Breadcrumbs" badge="Training"
            desc="Four short walkthroughs on the core of Breadcrumbs, in 5 minutes or less. Watch now or come back to these later."
            done={taskDone.videos} locked={isLocked('videos')} active={isActive('videos')}
          >
            {isActive('videos') && (
              <div className="ob-drawer show">
                <div className="ob-vid-list">
                  {TRAINING_VIDEOS.map(vid => (
                    <button key={vid.id} className={`ob-vid-row${watchedVids[vid.id]?' watched':''}`} onClick={()=>openVideo(vid)}>
                      <div className="ob-vr-play">
                        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                      </div>
                      <div className="ob-vr-body">
                        <div className="ob-vr-t">{vid.title}</div>
                      </div>
                      <div className="ob-vr-right">
                        <div className="ob-vr-check">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="ob-drawer-foot">
                  <div className="df-note">Watch now, or continue and come back later.</div>
                  <button className="ob-mark-done"
                    onClick={()=>{ setTaskDone(p=>({...p,videos:true})); }}>
                    {Object.keys(watchedVids).length>=TRAINING_VIDEOS.length?'Continue':Object.keys(watchedVids).length>0?'Continue':'Skip for now'}
                  </button>
                </div>
              </div>
            )}
          </PortalTask>

          {/* Task 6 – SLA */}
          <PortalTask
            num={6} title="Review your SLA" badge="Agreement"
            desc="Your commitment to excellence begins here. Learn the standards our team upholds to be part of the program, and sign at the bottom."
            done={taskDone.sla} locked={isLocked('sla')} active={isActive('sla')}
          >
            {isActive('sla') && (
              <div className="ob-drawer show">
                <button className="ob-sla-open-btn" onClick={() => setSlaModal(true)}>
                  Open the agreement
                </button>
                <div className="ob-sla-status-row">
                  <div className={`ob-sla-check-icon${slaReady ? ' done' : ''}`}>
                    {slaReady && (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="13" height="13">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </div>
                  <span className="ob-sla-status-txt">Initial each point and sign to complete</span>
                </div>
                <div className="ob-drawer-foot">
                  <div className="df-note">Review and sign the agreement to continue.</div>
                  <button className="ob-mark-done" disabled={!slaReady}
                    onClick={() => setTaskDone(p => ({...p, sla: true}))}>
                    Continue
                  </button>
                </div>
              </div>
            )}
          </PortalTask>

          {/* Graduate block */}
          <div className={`ob-graduate${requiredDone?' ready':''}`}>
            <div className="g-lock">
              {requiredDone
                ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="28" height="28"><path d="M20 6L9 17l-5-5"/></svg>
                : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="28" height="28"><rect x="4" y="10" width="16" height="10" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>
              }
            </div>
            <div className="gt">Graduate to full access</div>
            <div className="gd">
              {requiredDone
                ? 'Setup complete. Welcome to the team.'
                : 'Finish all setup steps to unlock the full Breadcrumbs workspace.'}
            </div>
            <button className="gbtn" disabled={!requiredDone} onClick={()=>window.location.href='/'}>
              Enter Breadcrumbs
            </button>
          </div>
        </div>
      </div>

      {/* ─── Script Modal (full screen) ─── */}
      <div className={`ob-script-modal${scriptModal?' show':''}`}>
        <div className="ob-script-modal-head">
          <span className="smh-t">Sales Script</span>
          <button className="ob-script-modal-close" onClick={()=>setScriptModal(false)}>Close</button>
        </div>
        <div className="ob-doc-scroll" onScroll={e=>{
          const el=e.target;
          if(el.scrollHeight-el.scrollTop-el.clientHeight<40) setScriptScrolled(true);
        }}>
          <ScriptDoc firstName={firstName} />
        </div>
        <div className={`ob-script-complete-bar${scriptScrolled?' show':''}`}>
          <button className="ob-script-complete-btn" onClick={()=>setScriptModal(false)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18"><polyline points="20 6 9 17 4 12"/></svg>
            Done reading — close script
          </button>
        </div>
      </div>

      {/* ─── Video Modal ─── */}
      <div className={`ob-vmodal${vidOpen?' show':''}`}>
        <div className="ob-vmodal-inner">
          <video
            key={vidOpen?.id}
            ref={vidRef}
            src={vidOpen?.link}
            controls
            className="ob-vid-player"
            onEnded={() => { if (vidOpen) setWatchedVids(w => ({...w, [vidOpen.id]: true})); }}
          />
          <div className="ob-vmodal-foot">
            <div className="vf-t">{vidOpen?.title}</div>
            <button className="ob-vmodal-close" onClick={closeVid}>Close</button>
          </div>
        </div>
      </div>

      {/* ─── SLA Modal ─── */}
      <div className={`ob-sla-modal${slaModal?' show':''}`}>
        <div className="ob-sla-window">
          <div className="ob-sla-modal-head">
            <span className="smh-t">Service Level Agreement</span>
            <button className="ob-script-modal-close" onClick={()=>setSlaModal(false)}>Close</button>
          </div>
          <div className="ob-sla-modal-body">
            <p className="ob-sla-intro">
              Your commitment to excellence begins here. These are the standards our team upholds to be part of the program. Initial each point to acknowledge it, then sign at the bottom to accept.
            </p>
            {SLA.map((clause,i) => (
              <div className="ob-sla-point" key={i}>
                <div className="ob-sla-num">{i+1}</div>
                <div className="ob-sla-point-body">
                  <div className="ob-sla-point-title">{clause.b}</div>
                  <div className="ob-sla-point-text">{clause.t}</div>
                  <div className="ob-sla-init-row">
                    <span className="il">Initial here</span>
                    <input
                      className={`ob-sla-init${slaInitials[i].trim()?' filled':''}`}
                      maxLength={4} value={slaInitials[i]} placeholder="—"
                      onChange={e=>{ const v=[...slaInitials]; v[i]=e.target.value.toUpperCase(); setSlaInitials(v); }}
                    />
                    <div
                      className={`ob-sla-tick${slaTicked[i]?' on':''}`}
                      style={{cursor:'pointer'}}
                      onClick={()=>{
                        const v=[...slaTicked]; v[i]=!v[i]; setSlaTicked(v);
                        if (v[i]) {
                          const ni=[...slaInitials];
                          if (!ni[i].trim()) {
                            ni[i]=((firstName[0]||'')+(lastName[0]||'')).toUpperCase();
                            setSlaInitials(ni);
                          }
                        }
                      }}
                    >
                      <div className="tb">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div className="ob-sla-sign-block">
              <h3>Signature</h3>
              <div className="ob-sla-sign-sub">By signing below, I confirm I have read, understood, and agree to each commitment above.</div>
              <div className="ob-sla-pad-wrap">
                <SignPad onInk={()=>setSlaHasInk(true)} canvasRef={sigRef} />
                <div className="ob-sla-pad-line" />
                <div className="ob-sla-pad-hint" style={{opacity:slaHasInk?0:1}}>SIGN HERE</div>
              </div>
              <div className="ob-sla-sign-meta">
                <button className="ob-sla-sign-clear" onClick={clearSig}>Clear</button>
                <div className="ob-sla-sign-name">{firstName} {lastName}</div>
                <div className="ob-sla-sign-date">{todayStr()}</div>
              </div>
              {slaError && <div className="ob-sla-error">{slaError}</div>}
              <button className="ob-sla-accept" disabled={!slaReady||slaSubmitting} onClick={()=>{ setSlaError(''); handleSlaSubmit(); }}>
                {slaSubmitting?'Submitting…':'Accept & sign agreement'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Signup ────────────────────────────────────────────────────────────────────
  return (
    <div className="ob-page">
      <div className="ob-grain" />
      <div className="ob-shell">
        {/* Topbar */}
        <div className="ob-topbar">
          <div className="ob-brand">
            <img src={breadcrumbLogo} alt="Breadcrumbs" className="ob-brand-logo" />
          </div>
          <button className="ob-exit">Save &amp; exit</button>
        </div>

        <div className="ob-stage">
          {/* Heading — only on step 0 */}
          {step === 0 && (
            <div className="ob-head">
              <div className="ob-title">Create your account</div>
            </div>
          )}

          {/* Trail */}
          <div className="ob-trail">
            {STEPS.map((s,i) => (
              <React.Fragment key={s}>
                <div className={`ob-crumb${i<step?' done':''}${i===step?' active':''}`} />
                {i<STEPS.length-1 && <div className={`ob-trail-link${i<step?' done':''}`}><i /></div>}
              </React.Fragment>
            ))}
          </div>

          {/* ── Step 0: Contact ── */}
          {step===0 && (
            <div className="ob-card">
              {/* Phone */}
              <div className="ob-field">
                <label className="ob-fl">Mobile number</label>
                <div className="ob-verify-row">
                  <input className={`ob-input${phoneOk?' ok':''}`} type="tel"
                    value={phone} onChange={e => setPhone(e.target.value)} disabled={phoneOk}
                  />
                  {!phoneOk && (
                    <button className={`ob-btn-ghost${!phoneSent&&phoneCD===0&&!otpSending.phone?' send-cta':''}`} disabled={phoneCD>0||otpSending.phone} onClick={()=>sendOtp('phone')}>
                      {phoneCD>0?`${phoneCD}s`:otpSending.phone?'Sending…':'Send code'}
                    </button>
                  )}
                </div>
                {otpSendErr.phone && <div className="ob-otp-err">{otpSendErr.phone}</div>}
                {phoneSent && !phoneOk && (
                  <>
                    <OtpBoxes value={phoneCode} onChange={v=>setPhoneCode(v)} onComplete={v=>verifyOtp('phone',v)} />
                    {otpVerifyErr.phone
                      ? <div className="ob-otp-err">{otpVerifyErr.phone}</div>
                      : <div className="ob-hint">{otpVerifying.phone?'Verifying…':'Enter the code sent to your phone.'}</div>}
                  </>
                )}
                <div className={`ob-verified${phoneOk?' show':''}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15"><polyline points="20 6 9 17 4 12"/></svg>
                  Phone verified
                </div>
              </div>

              {/* Email */}
              {hasEmail && (
              <div className="ob-field">
                <label className="ob-fl">Personal email</label>
                <div className="ob-verify-row">
                  <input className={`ob-input${emailOk?' ok':''}`} type="email"
                    value={email} onChange={e => setEmail(e.target.value)} disabled={emailOk}
                  />
                  {!emailOk && (
                    <button className={`ob-btn-ghost${!emailSent&&emailCD===0&&!otpSending.email?' send-cta':''}`} disabled={emailCD>0||otpSending.email} onClick={()=>sendOtp('email')}>
                      {emailCD>0?`${emailCD}s`:otpSending.email?'Sending…':'Send code'}
                    </button>
                  )}
                </div>
                {otpSendErr.email && <div className="ob-otp-err">{otpSendErr.email}</div>}
                {emailSent && !emailOk && (
                  <>
                    <OtpBoxes value={emailCode} onChange={v=>setEmailCode(v)} onComplete={v=>verifyOtp('email',v)} />
                    {otpVerifyErr.email
                      ? <div className="ob-otp-err">{otpVerifyErr.email}</div>
                      : <div className="ob-hint">{otpVerifying.email?'Verifying…':'Enter the code sent to your email.'}</div>}
                  </>
                )}
                <div className={`ob-verified${emailOk?' show':''}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15"><polyline points="20 6 9 17 4 12"/></svg>
                  Email verified
                </div>
              </div>
              )}
            </div>
          )}

          {/* ── Step 1: Identity ── */}
          {step===1 && (
            <div className="ob-card">
              <div className="ob-row" style={{marginBottom:'16px'}}>
                <div className="ob-field">
                  <label className="ob-fl">First name</label>
                  <input className="ob-input" placeholder="Jane" value={firstName} onChange={e=>setFirstName(e.target.value)} />
                </div>
                <div className="ob-field">
                  <label className="ob-fl">Last name</label>
                  <input className="ob-input" placeholder="Smith" value={lastName} onChange={e=>setLastName(e.target.value)} />
                </div>
              </div>
              <div className="ob-field">
                <label className="ob-fl">Date of birth</label>
                <input className="ob-input" type="date" value={dob} onChange={e=>setDob(e.target.value)} style={{colorScheme:'light'}} />
              </div>
              <div className="ob-field">
                <label className="ob-fl">Password</label>
                <div className="ob-pass-wrap">
                  <input
                    className={`ob-input${pwStrength===5?' ok':password?' err':''}`}
                    type={showPw?'text':'password'} placeholder="Create a password"
                    value={password} onChange={e=>setPassword(e.target.value)}
                  />
                  <button className={`ob-pass-eye${showPw?' on':''}`} type="button" onClick={()=>setShowPw(v=>!v)}>
                    {showPw
                      ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    }
                  </button>
                </div>
                <>
                  <div className="ob-meter">
                    <i style={{
                      width:`${pwStrength*20}%`,
                      background:['#5c5c5c','#e2574a','#e2574a','#e8a020','#7fae6a','var(--ok)'][pwStrength],
                    }} />
                  </div>
                  {password ? (
                    <div className="ob-pw-rules">
                      {[
                        { label: 'More than 12 characters', met: password.length > 12 },
                        { label: 'Uppercase letter',         met: /[A-Z]/.test(password) },
                        { label: 'Lowercase letter',         met: /[a-z]/.test(password) },
                        { label: 'Number',                   met: /[0-9]/.test(password) },
                        { label: 'Special character',        met: /[^A-Za-z0-9]/.test(password) },
                      ].map(({ label, met }) => (
                        <div key={label} className={`ob-pw-rule${met ? ' met' : ' fail'}`}>
                          <span className="ob-pw-rule-icon">
                            {met
                              ? <svg viewBox="0 0 10 10" fill="none" stroke="#fff" strokeWidth="1.8" width="8" height="8"><polyline points="1.5,5 4,7.5 8.5,2.5"/></svg>
                              : <svg viewBox="0 0 10 10" fill="none" stroke="#fff" strokeWidth="1.8" width="8" height="8"><line x1="2" y1="2" x2="8" y2="8"/><line x1="8" y1="2" x2="2" y2="8"/></svg>
                            }
                          </span>
                          {label}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="ob-meter-label">Use 12+ characters with uppercase, lowercase, number and special character.</div>
                  )}
                </>
              </div>
              <div className="ob-field">
                <label className="ob-fl">Your username</label>
                <div className="ob-username-chip">
                  <span className="u">{firstName || lastName ? `@${username}` : '—'}</span>
                  <span className="tag">auto-generated</span>
                </div>
                <div className="ob-hint">First initial plus last name. This is how your team will find you.</div>
              </div>
            </div>
          )}

          {/* ── Step 2: Photo ── */}
          {step===2 && (
            <div className="ob-card">
              <div className="ob-step-title">Add your photo</div>
              <div className="ob-step-desc">Upload your favorite photo or headshot. This appears on your profile, the leaderboard, and your deals.</div>
              <div
                className={`ob-upload${photo?' has':''}${dragOver?' drag':''}`}
                onClick={()=>!photo && fileRef.current?.click()}
                onDragOver={e=>{e.preventDefault();setDragOver(true);}}
                onDragLeave={()=>setDragOver(false)}
                onDrop={e=>{
                  e.preventDefault();setDragOver(false);
                  const file=e.dataTransfer.files?.[0];
                  if(file?.type.startsWith('image/')) handleFile(file);
                }}
              >
                {!photo ? (
                  <>
                    <div className="ic">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="40" height="40">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                    </div>
                    <div className="big">Drop a photo or click to upload</div>
                    <div className="sm">JPG or PNG, max 8 MB</div>
                  </>
                ) : (
                  <div className="ob-preview-wrap show">
                    <img src={photo} alt="preview" />
                    <div className="ob-preview-meta">
                      <div className="n">Photo uploaded</div>
                      <div className="s">Looks great!</div>
                    </div>
                    <button className="ob-preview-re" onClick={e=>{e.stopPropagation();setPhoto(null);setPhotoFile(null);}}>Change</button>
                  </div>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" style={{display:'none'}} onChange={e=>handleFile(e.target.files?.[0])} />
              {/* {photo && ( */}
                <div className="ob-ai-hint">
                  <span className="spark">✦</span>
                  <span>Not a professional shot? No problem. We'll use AI to generate a polished, high-quality version in formal wear.{' '}
                    <span className="peek" onClick={()=>setShowAiPop(true)}>See an example</span>
                  </span>
                </div>
              {/* )} */}
            </div>
          )}

          {/* ── Step 3: Path ── */}
          {step===3 && (
            <div className="ob-card">
              <div className="ob-step-title">Which path are you on?</div>
              <div className="ob-paths">
                {[
                  {key:'mortgage',    label:'Mortgage',    desc:'Help people get pre-approved for home financing to buy homes and investments, and with other home finance needs.'},
                  {key:'real-estate', label:'Real Estate', desc:'Help people navigate buying and selling homes and investment property.'},
                  {key:'both',        label:'Both',        desc:'Get dually licensed and help people with both aspects of the transaction.'},
                ].map(p=>(
                  <button key={p.key} className={`ob-path${path===p.key?' sel':''}`} onClick={()=>setPath(p.key)}>
                    <div className={`pic ${p.key !=='both' ? "pic-img" : ""}`}>
                      {p.key==='mortgage' && (
                        <img src="https://assets.cdn.filesafe.space/lmdPZBnct1qETepFwbdi/media/6a68d49cce3f1b19d562ff03.png" alt=""/>
                        )}
                      {p.key==='real-estate' &&  (
                        <img src="https://assets.cdn.filesafe.space/lmdPZBnct1qETepFwbdi/media/6a68d49ca0ca605ff39e31d7.png" alt=""/>
                      )}
                      {p.key==='both' && (
                        <div className="pic "><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="12" r="4"></circle><circle cx="16" cy="12" r="4"></circle></svg></div>
                      )}
                      </div>
                    <div className="ptext">
                      <div className="pn">{p.label}</div>
                      <div className="pd">{p.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Step 4: Languages ── */}
          {step===4 && (
            <div className="ob-card">
              <div className="ob-step-title">What languages do you speak?</div>
              <div className="ob-step-desc">Add <b style={{color:'var(--txt)'}}>every</b> language you speak, even conversationally. Bilingual agents get matched to more leads and future opportunities. We started you with English. Remove it if it doesn't apply.</div>
              <div className="ob-chips">
                {langs.map(l=>(
                  <div key={l} className="ob-chip">
                    {l}
                    {l!=='English' && <button onClick={()=>removeLang(l)}>×</button>}
                  </div>
                ))}
              </div>
              <div className="ob-lang-wrap" ref={langRef}>
                <input className="ob-lang-input" placeholder="Add a language…"
                  value={langSearch}
                  onChange={e=>{setLangSearch(e.target.value);setLangDropOpen(true);}}
                  onFocus={()=>setLangDropOpen(true)}
                />
                <div className={`ob-suggest${langDropOpen&&langOptions.length>0?' show':''}`}>
                  {langOptions.slice(0,8).map(l=>(
                    <button key={l} onMouseDown={()=>addLang(l)}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="ob-hint">Start typing and pick from the list, or press Enter to add your own.</div>
            </div>
          )}

          {/* Nav */}
          {submitError && (
            <div className="ob-submit-error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {submitError}
            </div>
          )}
          <div className="ob-nav">
            <button className={`ob-back${step===0?' hidden':''}`} onClick={goBack}>← Back</button>
            <button className="ob-next" disabled={!canGoNext||submitting} onClick={()=>{ setSubmitError(''); goNext(); }}>
              {submitting?'Creating account…':step===STEPS.length-1?'Create account':'Continue'}
            </button>
          </div>
        </div>
      </div>

      {/* ─── AI Photo Pop ─── */}
      <div className={`ob-ai-pop${showAiPop?' show':''}`} onClick={e=>{if(e.target===e.currentTarget)setShowAiPop(false);}}>
        <div className="pop-title">Casual photo? We'll handle it.</div>
        <div className="ob-compare">
          <div>
            <div className="ph">
              <img src={"https://assets.cdn.filesafe.space/lmdPZBnct1qETepFwbdi/media/6a68d4cfb7fe5a8e31c70289.jpg"} alt="Before" />
            </div>
            <div className="lab">Your upload</div>
          </div>
          <div>
            <div className="arrow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
            </div>
            <div className="ph">
              <img src={"https://assets.cdn.filesafe.space/lmdPZBnct1qETepFwbdi/media/6a68d4cf18a264df538f8b13.jpg"} alt="After" />
            </div>
            <div className="lab after">AI-enhanced</div>
          </div>
        </div>
        <div className="cap">Same you, enhanced with studio lighting and formal wear.</div>
        <button className="pop-close" onClick={()=>setShowAiPop(false)}>Got it</button>
      </div>
    </div>
  );
}
